import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Rss } from "lucide-react";
import { McpRequestDialog } from "@/components/mcp-request-dialog";
import { EditorialTicker } from "@/components/editorial-ticker";
import { AuroraBackground } from "@/components/aurora-background";
import { MarketReportsGrid } from "@/components/market-reports-grid";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getAllMarketReports, type MarketReport } from "@/lib/market-reports.functions";
import { seo } from "@/lib/seo";
import { breadcrumb, collectionPage } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";

const LOOPS_FORM_URL = "https://app.loops.so/api/newsletter-form/cmobl75n100ts0i3czaj7y5es";

type LoaderData = { reports: MarketReport[] };

export const Route = createFileRoute("/market-reports/")({
  loader: async (): Promise<LoaderData> => {
    const reports = await getAllMarketReports();
    return { reports };
  },
  head: ({ loaderData }) => {
    const reports = (loaderData as LoaderData | undefined)?.reports ?? [];
    return seo({
      title: "Real-Time Market Intelligence Drops & Signal Feeds",
      description:
        "Access daily data-driven market reports. Connecting autonomous AI agents and technical founders to over 10,000 real-time industry signals and structured context feeds.",
      path: "/market-reports",
      type: "website",
      jsonLd: [
        collectionPage({
          name: "Axy.digital Digital Market Intelligence",
          description:
            "Daily programmatic market reports across agentic, decentralized, and AI infrastructure markets.",
          url: `${SITE_URL}/market-reports`,
          items: reports
            .filter((r) => r.slug?.current)
            .map((r) => ({
              name: r.title,
              url: `/market-reports/${r.slug!.current}`,
            })),
        }),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: "Market Reports", url: "/market-reports" },
        ]),
      ],
    });
  },
  errorComponent: ({ error }) => (
    <section className="px-6 py-24 text-center text-white/70">{error.message}</section>
  ),
  notFoundComponent: () => (
    <section className="px-6 py-24 text-center text-white/70">Page not found.</section>
  ),
  component: MarketReportsPage,
});

function relativeFromNow(iso: string): string {
  const diffMs = Date.now() - Date.parse(iso);
  if (!Number.isFinite(diffMs) || diffMs < 0) return "moments ago";
  const mins = Math.round(diffMs / 60000);
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

type SubscribeStatus = { type: "success" | "error"; message: string } | null;

function SubscribeForm({ className }: { className?: string }) {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<SubscribeStatus>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const email = (form.elements.namedItem("email") as HTMLInputElement | null)?.value;
    if (!email) return;

    setSubmitting(true);
    setStatus(null);
    try {
      const res = await fetch(LOOPS_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: `userGroup=&mailingLists=&email=${encodeURIComponent(email)}`,
      });
      if (res.ok) {
        setStatus({ type: "success", message: "Thanks — you're subscribed." });
        form.reset();
      } else {
        const data = (await res.json().catch(() => null)) as { message?: string } | null;
        setStatus({
          type: "error",
          message: data?.message ?? "Something went wrong, please try again.",
        });
      }
    } catch {
      setStatus({ type: "error", message: "Something went wrong, please try again." });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <form className={className} onSubmit={handleSubmit}>
        <Input
          type="email"
          name="email"
          required
          placeholder="you@company.com"
          aria-label="Email address"
          className="h-11 bg-white/5 border-white/20 text-white placeholder:text-white/40"
        />
        <Button type="submit" disabled={submitting} className="h-11 px-6">
          {submitting ? "Subscribing…" : "Subscribe"}
        </Button>
      </form>
      {status && (
        <p
          role="status"
          aria-live="polite"
          className={`mt-3 text-sm ${status.type === "success" ? "text-emerald-300" : "text-red-300"}`}
        >
          {status.message}
        </p>
      )}
    </div>
  );
}

function MarketReportsPage() {
  const { reports } = Route.useLoaderData() as LoaderData;
  const latest = reports[0];
  // Computed client-side only: relativeFromNow() reads Date.now(), which would
  // differ between the SSR render and hydration (and go stale with cached HTML).
  const [lastGenerated, setLastGenerated] = useState("recently");
  useEffect(() => {
    if (latest) setLastGenerated(relativeFromNow(latest.publishedAt));
  }, [latest]);

  return (
    <article className="text-white">
      <EditorialTicker />

      {/* Hero */}
      <section className="relative px-6 pt-16 pb-12 overflow-hidden">
        <AuroraBackground />
        <div className="relative max-w-5xl mx-auto">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-200 shadow-[0_0_30px_rgba(16,185,129,0.25)]">
            <span className="relative flex size-2">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
              <span className="relative rounded-full bg-emerald-400 size-2" />
            </span>
            <span className="tracking-wide">
              LIVE DATA APIS CONNECTED · Last Report Generated {lastGenerated}
            </span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-balance mb-6">
            Autonomous <span className="text-gradient">Market Intelligence</span> Drops
          </h1>
          <p className="text-lg md:text-xl text-white/75 max-w-3xl leading-relaxed">
            Daily, data-driven syndications mapping programmatic shifts, liquidity movements, and
            infrastructural trends across emerging agentic and decentralized markets. Zero manual
            bias. Pure algorithmic signal.
          </p>

          {/* Subscribe — main CTA */}
          <div className="mt-8 max-w-xl">
            <SubscribeForm className="flex flex-col sm:flex-row gap-2" />
            <p className="mt-3 text-sm text-white/55">
              Get Axy.digital market reports delivered straight to your inbox — daily.
            </p>
          </div>
        </div>
      </section>

      {/* Grid */}
      <MarketReportsGrid items={reports} />

      {/* Secondary CTAs */}
      <section className="px-6 pb-24">
        <div className="max-w-5xl mx-auto rounded-lg border border-white/10 bg-white/[0.03] p-6 md:p-8">
          <p className="text-sm md:text-base text-white/75 leading-relaxed">
            Connect your AI agents directly to live industry intelligence. Axy.digital continuously
            aggregates and analyzes over{" "}
            <span className="text-white font-semibold">10,000 multi-source market signals</span>,
            transforming raw data into structured, real-time context feeds and deep-dive reports for
            your agents to query instantly or make decisions based on accurate real-time data.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <McpRequestDialog />
            <a
              href="/market-reports/feed.xml"
              className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-pink-soft transition-colors"
            >
              <Rss className="size-3.5" aria-hidden />
              Publisher Center RSS Feed (feed.xml)
            </a>
          </div>
        </div>
      </section>
    </article>
  );
}
